import React, { ElementType, FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import useMediaQuery from '@mui/material/useMediaQuery';

import { fetch_one, fetch_all_with_count } from "../../lib/v31lib";
import { loginAtom } from '../../lib/auth';
import { currentPracticeAtom } from '../../lib/practice';
import HelpersPractice from '../../actions/helpers/practice';

import AccountBox from '@mui/icons-material/AccountBox';
import DeleteIcon from '@mui/icons-material/Delete';
import InboxIcon from '@mui/icons-material/Inbox';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ReplyIcon from '@mui/icons-material/Reply';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import PrintIcon from '@mui/icons-material/Print';

import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Grid,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Divider,
  DialogActions,
  Alert,
  Tooltip,
} from '@mui/material';

import {
  Button,
  Dt,
  Fab,
  Page,
  TextInput,
  Typography
} from '../../components/v2/styled';

import { Base as DialogBase } from '../../components/v2/dialogs/Base';
import { ZorgMail } from '../../models/ZorgMail';
import { AttachmentClient } from '../../components/v2/AttachmentClient';
import { AttachmentClientZorgmail } from '../../components/v2/AttachmentClientZorgmail';
import { User } from '../../models/User';

const { DateTime } = require("luxon");

type Props = {};

export const CoachZorgMail: FunctionComponent<Props> = ({}) => {
  const theme = useTheme();
  const [login] = useAtom(loginAtom);
  const [currentPractice] = useAtom(currentPracticeAtom);
  const { t, i18n } = useTranslation(['translations']);
  const navigate = useNavigate();
  const { state } = useLocation();

  const is_zorg_mail = HelpersPractice.practice_modules_v3({ practice: currentPractice, check: 'zorg_mail' });

  const [search, setSearch] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<string>('inbox');
  const [page, setPage] = useState<number>(0);
  const [activeMail, setActiveMail] = useState<ZorgMail>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [activeClient, setActiveClient] = useState<undefined|{id: number; name: string;}>({
    id: state?.user_id?.id,
    name: state?.user_id?.name
  });
  
  // Use state or ref to track mailCount, similar to other components
  const [mailCount, setMailCount] = useState<number>(0);

  // We fetch unread mails first for the inbox view, similar to standard Inbox
  const {
    data: unreadMails,
    isSuccess: unreadMailsSuccess,
    refetch: unreadMailsRefetch,
  } = useQuery({
    queryKey: ["zorg_unread_mails", login?.id, "tab", 'inbox', "page", 0, { search: search, user_id: activeClient?.id }],
    queryFn: () =>
      fetch_all_with_count<ZorgMail>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'zorg_mail',
          fields: [
            'id', 'user_id', 'user', 'is_read', 'is_deleted', 'is_sending', 'is_sent', 'read', 'message_id', 'message_data', 'content', 'created_at'
          ],
          sub_fields: {
            user: ['id', 'full_name']
          },
          per_page: 200,
          page: 0,
          order: 'created_at DESC',
          filter: {
            search: search,
            advanced: {
              user_id: login?.id,
              is_deleted: '0',
              is_sending: '0',
              is_read: 0,
              client_id: activeClient?.id
            }
          }
        },
        login
      ),
    select: (d) => d.result,
    enabled: !!login && !!is_zorg_mail && currentTab === 'inbox',
  });

  const {
    data: mails,
    isLoading: mailsLoading,
    isSuccess: mailsSuccess,
    refetch: mailsRefetch,
  } = useQuery({
    queryKey: ["zorg_mails", login?.id, "tab", currentTab, "page", page, { search: search, user_id: activeClient?.id }],
    queryFn: () =>
      fetch_all_with_count<ZorgMail>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'zorg_mail',
          fields: [
            'id', 'user_id', 'user', 'is_read', 'is_deleted', 'is_sending', 'is_sent', 'read', 'message_id', 'message_data', 'content', 'created_at'
          ],
          sub_fields: {
            user: ['id', 'full_name']
          },
          per_page: 20,
          page: page,
          order: 'created_at DESC',
          filter: {
            search: search,
            advanced: currentTab === 'inbox' ? {
              user_id: login?.id,
              is_deleted: '0',
              is_sending: '0',
              is_read: 1,
              client_id: activeClient?.id
            } : (currentTab === 'sent' ? {
              user_id: login?.id,
              is_sent: 1,
              is_deleted: '0',
              client_id: activeClient?.id
            } : (currentTab === 'drafts' ? {
              user_id: login?.id,
              is_deleted: '0',
              is_sending: 1,
              is_sent: '0',
              client_id: activeClient?.id
            } : (currentTab === 'deleted' ? {
              is_deleted: 1,
              pov: 1,
              client_id: activeClient?.id
            } : ({}))))
          }
        },
        login
      ),
    enabled: !!login && !!is_zorg_mail,
  });

  const {
    data: preloadedClient,
    // isLoading: clientsLoading,
    // isError: clientsError,
    // isSuccess: clientsSuccess,
    // refetch: clientsRefetch,
  } = useQuery({
    queryKey: ["clients", 'full_bsn', currentPractice?.id, activeClient?.id],
    queryFn: () =>
      fetch_one<User>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'user',
          fields: [
            'id', 'full_name', 'first_name', 'last_login_date', 'language_id', 'email', 'national_number', 'last_name', 'street', 'number', 'zip', 
            'city', 'birth_date', 'phone_mobile', 'gender', 'extra_data', 'country|id_name'
          ],
          id: activeClient?.id
        },
        login
      ),
    enabled: !!currentPractice?.id && !!activeClient?.id
  });

  useEffect(() => {
    if (mailsSuccess && mails) {
      setMailCount(mails.count || 0);
    }
  }, [mails, mailsSuccess]);

  const mutationCustomAction = useMutation({
    mutationFn: (pars: { formData: any }) => {
      return fetch_one<{
        custom_result: {
          success: boolean,
          error?: string,
          mail: ZorgMail
        }
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onSuccess: (data, variables) => {
      mailsRefetch();
      unreadMailsRefetch();
      if (variables.formData.unread === 1 || variables.formData.deleted === 1 || variables.formData.undeleted === 1) {
        setIsDialogOpen(false);
        setActiveMail(undefined);
      } else if (data?.custom_result?.mail) {
        setActiveMail(data.custom_result.mail);
      }
    },
  });

  const handleMarkAs = (mailId: number, updates: { read?: number; unread?: number; deleted?: number; undeleted?: number }) => {
    mutationCustomAction.mutate({
      formData: {
        object: 'zorg_mail',
        class_action: 'custom_api_mark_as',
        id: mailId,
        fields: [
          'id', 'user_id', 'user', 'is_read', 'is_deleted', 'is_sending', 'is_sent', 'message_id', 'message_data', 'content', 'created_at'
        ],
        sub_fields: {
          user: ['id', 'full_name']
        },
        ...updates
      }
    });
  };

  useEffect(() => {
    if (!!is_zorg_mail) {
      mailsRefetch();
      unreadMailsRefetch();
    }
  }, [page, currentTab, is_zorg_mail]);

  useEffect(() => {
    if (!is_zorg_mail) return;
    let to = setTimeout(() => {
      mailsRefetch();
      unreadMailsRefetch();
    }, 2000);
    return () => clearTimeout(to);
  }, [search]);

  // When a mail is opened, mark it as read if it is unread and addressed to the user
  useEffect(() => {
    if (!!activeMail && !activeMail.read) {
      handleMarkAs(activeMail.id!, { read: 1 });
    }
  }, [activeMail]);

  const doMenuItem = (key: string, label: string, Icon: ElementType) => {
    return (
      <MenuItem
        onClick={() => {
          setCurrentTab(key);
          setPage(0);
        }}
        sx={{
          borderRadius: 5,
          transition: "background-color 0.5s ease",
          backgroundColor: currentTab === key ? theme.palette.primary.main : 'transparent',
          color: currentTab === key ? 'white' : 'rgba(0, 0, 0, 0.87)',
          "& span, & svg": {
            transition: "color 0.25s ease"
          },
          "&:hover, &.is-active": {
            backgroundColor: theme.palette.primary.main,
            "& span, & svg": {
              color: `${theme.palette.primary.contrastText} !important`
            }
          },
        }}
      >
        <ListItemIcon>
          <Icon
            fontSize="small"
            sx={{
              color: currentTab === key ? theme.palette.primary.contrastText : 'rgba(0, 0, 0, 0.87)'
            }}
          />
        </ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
    );
  };

  const print_mail = () => {
    var content = document.getElementById("zorgmail_print_contents");
    // @ts-ignore
    var pri = document.getElementById("zorgmail_if_printframe")?.contentWindow;
    if (pri && content) {
      pri.document.open();
      pri.document.write(`<pre>${content.innerHTML}</pre>`);
      pri.document.close();
      pri.focus();
      pri.print();
    }
  };

  const renderMailRow = (mail: ZorgMail, border: boolean, isUnreadList: boolean) => {
    const isUnread = isUnreadList || !mail.read;
    const showTo = currentTab === 'sent' || currentTab === 'drafts';
    const hasClient = !!mail?.message_data?.linked_client || !!mail?.message_data?.draft?.linked_client

    return (
      <Grid
        container
        spacing={0}
        key={mail.id}
        sx={{
          borderBottom: border ? '1px solid rgba(0, 0, 0, 0.2)' : 'none',
          paddingTop: '8px',
          paddingBottom: '8px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.02)'
          }
        }}
        onClick={() => {
          if (!!mail.is_sending && !mail.is_sent) {
            navigate(`/zorgmail_inbox/new`, {state: {load_id: mail.id}});
          } else {
            setActiveMail(mail);
            setIsDialogOpen(true);
          }
        }}
      >
        <Grid item xs={12} md={3}>
          <Box sx={{ fontSize: '0.9rem', pr: 1 }}>
            <Typography
              sx={{
                fontWeight: isUnread ? 'bold' : 'normal',
                height: '24px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap'
              }}
            >
              {/* {mail.replied && (
                <ReplyIcon
                  sx={{
                    display: 'inline-block',
                    marginRight: 1,
                    height: 20,
                    float: 'left'
                  }}
                />
              )} */}
              {showTo ? mail.message_data?.draft?.recipients?.map(fff => fff.displayName).join(", ") : mail.message_data?.header_from}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={9}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <Box sx={{ flexGrow: 1, minWidth: 0, pr: 2 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: isUnread ? 'bold' : 'normal'
                }}
              >
                {!!hasClient && <AccountBox
                  sx={{
                    display: 'inline-block',
                    marginRight: 1,
                    height: 20,
                    float: 'left',
                    color: (!!mail.message_data?.draft?.linked_client?.id || !!mail.message_data?.linked_client?.id) ? theme.palette.primary.main : 'grey'
                  }}
                />}
                <span>{(showTo ? mail.message_data?.draft?.subject : mail.message_data?.header_subject) || t("shared.zorg_mail.no_subject", "(Geen onderwerp)")}</span>
                <FiberManualRecordIcon
                  sx={{
                    fontSize: '0.3rem',
                    margin: '0 8px',
                    color: 'rgba(0, 0, 0, 0.2)',
                    verticalAlign: 'middle'
                  }}
                />
                <span style={{ color: 'rgba(0, 0, 0, 0.4)', fontWeight: 'normal' }}>
                  {((showTo ? mail?.message_data?.draft?.content : mail?.content) || '').replace(/<[^>]*>?/gm, '')}
                </span>
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: '70px' }}>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(0, 0, 0, 0.6)' }}>
                {DateTime.now().toFormat("D") === DateTime.fromISO(mail.created_at).toFormat("D") ? (
                  <Dt d={mail.created_at} f="t" />
                ) : (
                  <Dt d={mail.created_at} f="d LLL" />
                )}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  const mailList = (mails?.result || []) as ZorgMail[];

  return (
    <Page
      premiumGatekeeping={login?.type === "User::Coach" && !!is_zorg_mail}
      premiumMode='zorg_mail'
      sx={{ padding: { xs: 0, md: 3 } }}
    >
      <Grid container spacing={2}>
        {/* message here */}
        {(!login?.extra_data?.zorgmail?.username || !login?.extra_data?.zorgmail?.password) && <Grid item xs={12}>
          <Alert severity='warning'>
            <Typography>{t("shared.zorg_mail.note.not_set_up")}</Typography>
            <Button
              label={t("shared.zorg_mail.cta.goto_settings")}
              id="goto_settings"
              onClick={() => {
                navigate("/settings", {state: {tab: 4}});
              }}
              size='small'
            />
          </Alert>
        </Grid>}


        <Grid item xs={12} md={2}>
          <Paper sx={{ padding: 1, backgroundColor: 'rgba(255, 255, 255, 0.4)' }}>
            {doMenuItem('inbox', t("shared.zorg_mail.inbox", "Inbox"), InboxIcon)}
            {doMenuItem('sent', t("shared.zorg_mail.sent", "Verzonden"), SendIcon)}
            {doMenuItem('drafts', t("shared.zorg_mail.drafts", "Concepten"), InsertDriveFileIcon)}
            {doMenuItem('deleted', t("shared.zorg_mail.deleted", "Verwijderd"), DeleteIcon)}
          </Paper>
        </Grid>

        <Grid container spacing={2} item xs={12} md={10} sx={{ alignContent: 'baseline', pb: 2 }}>
          <Grid item xs={12}>
            <TextInput
              id="search"
              placeholder={t("shared.zorg_mail.search", "Zoeken")}
              value={search}
              muiIcon={<SearchIcon />}
              backend
              noCaption
              onChange={(v) => setSearch(v)}
              sx={{ marginTop: 0, marginBottom: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ padding: 2 }}>
              {mailsLoading && (
                <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {mailsSuccess && mailList.length < 1 && ((currentTab !== 'inbox') || (currentTab === 'inbox' && (!unreadMails || unreadMails.length < 1))) && (
                <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                  <Typography sx={{ fontStyle: 'italic', fontSize: '0.8rem', color: theme.palette.secondary.main }}>
                    {t("shared.zorg_mail.no_mails", "Geen berichten gevonden")}
                  </Typography>
                </Box>
              )}

              {currentTab === 'inbox' && page === 0 && unreadMailsSuccess && unreadMails && unreadMails.length > 0 && (
                <>
                  <Box sx={{ width: '100%', textAlign: 'left' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}>
                      {t("shared.zorg_mail.unread_messages", "Ongelezen berichten")}
                    </Typography>
                    {unreadMails.map((mail, i) => renderMailRow(mail, i !== unreadMails.length - 1, true))}
                  </Box>
                  {mailsSuccess && mailList && mailList.length > 0 && (
                    <Box sx={{ width: '100%', height: '2px', backgroundColor: 'rgba(0, 0, 0, 0.1)', my: 2 }} />
                  )}
                </>
              )}

              {mailsSuccess && mailList && mailList.length > 0 && (
                <Box sx={{ width: '100%', textAlign: 'left' }}>
                  {currentTab === 'inbox' && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
                      {t("shared.zorg_mail.all_messages", "Alle berichten")}
                    </Typography>
                  )}
                  {mailList.map((mail, i) => renderMailRow(mail, i !== mailList.length - 1, false))}
                </Box>
              )}
            </Paper>
          </Grid>

          {mailCount > 20 && (
            <Grid item xs={12}>
              <Stack spacing={2} alignItems="center">
                <Pagination
                  count={Math.ceil(mailCount / 20)}
                  page={page + 1}
                  onChange={(e, v) => setPage(v - 1)}
                  boundaryCount={1}
                  siblingCount={2}
                  size="small"
                />
              </Stack>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* ZorgMail Detail Dialog */}
      {!!activeMail && (
        <DialogBase
          name="zorgmail"
          title={activeMail.message_data?.draft?.subject || activeMail.message_data?.document_subject || t("shared.zorg_mail.no_subject", "(Geen onderwerp)")}
          open={isDialogOpen}
          setOpen={setIsDialogOpen}
          fullWidth
          // fullScreen={useMediaQuery(theme.breakpoints.down('md'))}
          maxWidth="md"
          content={
            <Grid container spacing={2} id="zorgmail_print_contents" sx={{ pt: 1 }}>
              <style type="text/css" media="print">
                {`img { max-width: 100%; }`}
              </style>
              <Grid item xs={12}>
                <Typography>
                  <span style={{ fontWeight: 'bold' }}>{t("shared.zorg_mail.sender", "Verzender")}:</span> {(!!activeMail.message_data?.draft ? t("general.terms.me") : undefined) || activeMail.message_data?.header_from}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  <span style={{ fontWeight: 'bold' }}>{t("shared.zorg_mail.recipient", "Ontvanger")}:</span> {activeMail.message_data?.draft?.recipients?.map(mm => mm.displayName).join(", ") || activeMail.message_data?.header_to}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  <span style={{ fontWeight: 'bold' }}>{t("shared.zorg_mail.time", "Tijdstip")}:</span> <Dt d={activeMail.created_at} f="DDD t" /> ({DateTime.fromISO(activeMail.created_at).toRelative({ locale: i18n.resolvedLanguage || 'nl' })})
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" html sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                  {activeMail.message_data?.draft?.content || activeMail.content}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                {!!activeMail.message_data?.linked_client && <AttachmentClientZorgmail
                  zmClientInfo={activeMail.message_data?.linked_client}
                />}
                {!!activeMail.message_data?.draft?.linked_client && <AttachmentClientZorgmail
                  zmClientInfo={{...activeMail.message_data?.draft?.linked_client, valid_practice: true}}
                />}
              </Grid>
            </Grid>
          }
          actions={
            <DialogActions sx={{ borderTop: '1px solid rgba(0,0,0,0.1)', px: 3, py: 2 }}>
              <MuiButton
                startIcon={<PrintIcon />}
                onClick={print_mail}
                color="primary"
              >
                {t("shared.zorg_mail.print", "Afdrukken")}
              </MuiButton>

              {activeMail.deleted ? (
                <MuiButton
                  startIcon={<RestoreFromTrashIcon />}
                  onClick={() => handleMarkAs(activeMail.id!, { undeleted: 1 })}
                  color="primary"
                >
                  {t("shared.zorg_mail.mark_as_undelete", "Herstellen")}
                </MuiButton>
              ) : (
                <MuiButton
                  startIcon={<DeleteIcon />}
                  onClick={() => handleMarkAs(activeMail.id!, { deleted: 1 })}
                  color="primary"
                >
                  {t("shared.zorg_mail.delete", "Verwijderen")}
                </MuiButton>
              )}

              {activeMail.read && (
                <MuiButton
                  startIcon={<MarkEmailUnreadIcon />}
                  onClick={() => handleMarkAs(activeMail.id!, { unread: 1 })}
                  color="primary"
                >
                  {t("shared.zorg_mail.mark_as_unread", "Markeren als ongelezen")}
                </MuiButton>
              )}

              <MuiButton
                onClick={() => setIsDialogOpen(false)}
                color="primary"
                variant="contained"
              >
                {t("shared.zorg_mail.close", "Sluiten")}
              </MuiButton>
            </DialogActions>
          }
        />
      )}

      <iframe id="zorgmail_if_printframe" title="Print ZorgMail" style={{ height: 0, width: 0, position: 'absolute', left: -1000, top: -1000 }} />

      {!(!login?.extra_data?.zorgmail?.username || !login?.extra_data?.zorgmail?.password) && <Fab
        onClick={() => {
          navigate("/zorgmail_inbox/new", {state: !!preloadedClient ? {linked_client: {
            id: preloadedClient?.id,
            birth_date: !!preloadedClient?.birth_date ? DateTime.fromFormat(preloadedClient?.birth_date, "y-LL-dd").toFormat("dd/LL/y") : '',
            gender: preloadedClient?.gender === 0 ? "V" : (preloadedClient?.gender === 1 ? "M" : "O"),
            man_name: !!preloadedClient.gender ? preloadedClient.last_name : '',
            man_name_pre: preloadedClient.extra_data?.dutch?.man_name_pre,
            girl_name: !preloadedClient.gender ? preloadedClient.last_name : '',
            girl_name_pre: preloadedClient.extra_data?.dutch?.girl_name_pre,
            voorletters: preloadedClient.extra_data?.dutch?.voorletters,
            first_name: preloadedClient.first_name,
            bsn: preloadedClient.national_number,
            street: preloadedClient.street,
            number: preloadedClient.number,
            zip: preloadedClient.zip,
            city: preloadedClient.city,
            province: preloadedClient.extra_data?.dutch?.province,
            country: preloadedClient?.country_name
          }} : {}});
        }}
      />}

      {!!activeClient?.id && <Box sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        backgroundColor: theme.palette.warning.main,
        width: '100%',
        padding: 0.5,
        zIndex: 1200
      }}>
        <Typography variant="caption">{t("coach.submenu.ehealth_mails.check_for_client_warning", "Je bent ehealth mails aan het checken voor")} {activeClient.name} <MuiButton
          sx={{fontSize: '0.7rem', marginLeft: 1}}
          size="small"
          // variant="outlined"
          onClick={(e) => {
            e.preventDefault();
            setActiveClient(undefined);
          }}
        >{t("coach.submenu.agenda.plan_for_client_cancel", "Stoppen")}</MuiButton></Typography>
      </Box>}
    </Page>
  );
};
